<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import Breadcrumbs from "@/components/admin/Breadcrumbs.vue";
import ModeToggleButton from "@/components/general/ModeToggleButton.vue";
import ThemeToggleButton from "@/components/general/ThemeToggleButton.vue";
import { currentMenuItems } from "@/composables/breadcrumbs";
import BaseRoutes from "@/enums/routes/BaseRoutes";
import AppSidebar from "@/layout/AppSidebar.vue";
import { useAuthStore } from "@/stores/authStore";

import { useLayout } from "./composables/layout";
import { useTheme } from "./composables/theme";

const { layoutConfig, layoutState, isSidebarActive, isMobile, toggleMobileMenu, closeMobileMenu } = useLayout();
const { isDark } = useTheme();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const outsideClickListener = ref<((ev: MouseEvent) => void) | null>(null);

function bindOutsideClickListener(): void {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        layoutState.overlayMenuActive = false;
        layoutState.overlaySubmenuActive = false;
        layoutState.menuHoverActive = false;
        layoutState.configSidebarVisible = false;
      }
    };
    document.addEventListener("click", outsideClickListener.value);
  }
}

function unbindOutsideClickListener(): void {
  if (outsideClickListener.value) {
    document.removeEventListener("click", outsideClickListener.value);
    outsideClickListener.value = null;
  }
}

function isOutsideClicked(event: MouseEvent): boolean {
  const sidebarEl = document.querySelector(".layout-sidebar");
  const topbarButtonEl = document.querySelector(".topbar-start > button");

  return !(
    sidebarEl?.isSameNode(event.target as Node) ||
    sidebarEl?.contains(event.target as Node) ||
    topbarButtonEl?.isSameNode(event.target as Node) ||
    topbarButtonEl?.contains(event.target as Node)
  );
}

watch(() => route.fullPath, closeMobileMenu);
watch(isMobile, closeMobileMenu);

watch(isSidebarActive, (newVal) => {
  if (newVal) {
    bindOutsideClickListener();
  } else {
    unbindOutsideClickListener();
  }
});

onMounted(async () => {
  authStore.load();
  try {
    await authStore.refreshPermissions();
  } catch (error) {
    console.error(error);
    console.warn("using cached permissions");
  }
});

onBeforeUnmount(() => {
  unbindOutsideClickListener();
});

const mobileMenuVisible = computed(() => isMobile.value && layoutState.mobileMenuActive);

const containerClass = computed(() => ({
  "layout-light": !isDark.value,
  "layout-dark": isDark.value,
  "layout-light-menu": !isDark.value,
  "layout-dark-menu": isDark.value,
  "layout-drawer": layoutConfig.menuMode === "drawer",
  "layout-sidebar-active": mobileMenuVisible.value || (!isMobile.value && layoutState.sidebarActive),
  "layout-sidebar-anchored": !isMobile.value && layoutState.anchored,
  "layout-mobile-active": mobileMenuVisible.value,
}));

function hideMenu(): void {
  document.body.click();
}

function signOut(): void {
  authStore.signOut();
  hideMenu();
  router.push({ name: BaseRoutes.SIGN_IN });
}
</script>

<template>
  <div class="layout-container" :class="containerClass">
    <AppSidebar />
    <div class="layout-content-wrapper">
      <div class="layout-content">
        <div class="flex items-center justify-between gap-2 px-2 py-1">
          <div class="flex items-center gap-1 min-w-0">
            <Button
              class="md:hidden! shrink-0"
              variant="text"
              icon="pi pi-bars"
              aria-label="Menu"
              @click="toggleMobileMenu"
            />
            <Breadcrumbs v-if="currentMenuItems.length > 0" />
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <ModeToggleButton variant="text" />
            <ThemeToggleButton />
            <Button v-tooltip="t('general.logout')" variant="text" icon="pi pi-sign-out" @click="signOut()" />
          </div>
        </div>
        <router-view />
      </div>
    </div>

    <div class="layout-mask" @click="closeMobileMenu" />
  </div>
  <Toast />
</template>

<style lang="scss"></style>
